import React, { FC, useContext, useEffect, useMemo } from 'react';

import { useFormikContext } from 'formik';
import _ from 'lodash';

// Constants
import styled from 'styled-components';

import aampeTheme from '../../../../../../constants/aampeTheme';
import ICONS from '../../../../../../constants/icons';

// Models
import { EditorContext } from '../../../../../../context/EditorContext';

import useDatasetsQuery from 'hooks/datasets/useDatasetByIdQuery';
import useDatasetContentQuery from 'hooks/datasets/useDatasetContentByIdQuery';

import { DatasetFiltersType } from '../../../../../../models/dataset-filters.model';
import { ElementKind } from '../../../../../../models/editor.model';
import { DatasetModel } from 'models/dataset.model';
import { FormulaTestMessageFormValues } from 'models/formula.model';

// Components
import TestFormulaElementField from '../TestFormulaElementField';

interface TestFormulaDatasetBlockProps {}

const TestFormulaDatasetBlock: FC<TestFormulaDatasetBlockProps> = () => {
    const { values, setFieldValue } =
        useFormikContext<FormulaTestMessageFormValues>();

    const {
        editorDetails: { formula, selectRandomFilters },
    } = useContext(EditorContext);

    const { data: filteredDataset, isLoading } = useDatasetsQuery(
        formula?.dataset_id || undefined,
    );
    const { data: filteredDatasetContent, isLoading: isContentLoading } =
        useDatasetContentQuery(formula?.dataset_id);

    const selectedDataset = useMemo<DatasetFiltersType | undefined>(() => {
        const datasetContent = (values as any)?.filters?.dataset_content;

        if (!datasetContent || _.isEmpty(datasetContent)) return undefined;

        return _.mapValues(datasetContent, (val) =>
            _.isArray(val) ? _.first(val) : val,
        );
    }, [values]);

    useEffect(() => {
        if (selectRandomFilters && !isContentLoading) {
            const randomIndex = Math.floor(
                Math.random() * (filteredDatasetContent?.length || 1),
            );
            const dataset = filteredDatasetContent?.[randomIndex];
            const filters: DatasetFiltersType = {};
            Object.entries(dataset?.content).forEach(
                ([key, value]) => (filters[key] = [String(value)]),
            );
            setFieldValue(`filters.dataset_content`, filters);
        }
    }, [selectRandomFilters, isContentLoading, filteredDatasetContent]);

    return (
        <Container>
            {formula?.dataset_id && !isLoading && (
                <TestFormulaElementField
                    type={ElementKind.dataset}
                    icon={<StyledDatasetIcon />}
                    name={'Dataset'}
                    elementId={formula?.dataset_id.toString()}
                    data={filteredDataset as DatasetModel}
                    selectedElement={selectedDataset}
                />
            )}
        </Container>
    );
};

export default TestFormulaDatasetBlock;

const Container = styled.div({
    display: 'flex',
    flexDirection: 'column',
    gap: aampeTheme.spacing4(6),
});

const StyledDatasetIcon = styled(ICONS.datasets)({
    width: '20px',
    height: '20px',
    color: aampeTheme.colors.textAccessibleWG01,
});
